import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, LogIn, Package } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [operatorInfo, setOperatorInfo] = useState(null);
  const [fetchingInfo, setFetchingInfo] = useState(false);

  useEffect(() => {
    if (!username) {
      setOperatorInfo(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const fetchInfo = async () => {
      setFetchingInfo(true);
      try {
        const response = await axios.get(`${API}/operators/${username}`, {
          signal: controller.signal,
        });
        if (!cancelled) {
          setOperatorInfo(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          setOperatorInfo(null);
        }
      } finally {
        if (!cancelled) {
          setFetchingInfo(false);
        }
      }
    };

    fetchInfo();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [username]);

  const handleSubmit = async () => {
    if (!username || !password) {
      toast.error("Por favor ingresa usuario y contraseña");
      return;
    }

    if (!operatorInfo) {
      toast.error("Operario no encontrado. Verifica el código.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
        tipoTrabajo: operatorInfo?.tipoTrabajo,
      });

      const {
        token,
        user,
        operario,
        operarioNombre,
        tipoTrabajo: tipoTrabajoBackend,
        estadoDestino,
      } = response.data;

      const tipoTrabajoFinal = (tipoTrabajoBackend || operatorInfo?.tipoTrabajo || "molduras").toLowerCase();

      if (
        operatorInfo?.tipoTrabajo &&
        tipoTrabajoBackend &&
        operatorInfo.tipoTrabajo !== tipoTrabajoBackend
      ) {
        toast.info(`Área ajustada automáticamente a ${tipoTrabajoFinal === 'molduras' ? 'Molduras' : 'Puertas'} según la configuración del operario`);
      }

      onLogin(
        token,
        user,
        operario,
        tipoTrabajoFinal,
        operarioNombre,
        estadoDestino
      );
      toast.success(
        `Inicio de sesión exitoso - ${
          operarioNombre ? `${operarioNombre}` : `Operario ${user}`
        } (${tipoTrabajoFinal === 'molduras' ? 'Molduras' : 'Puertas'})`
      );
    } catch (error) {
      console.error("Login error:", error);
      toast.error(
        error.response?.data?.detail || "Error al iniciar sesión"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="space-y-4 text-center pb-8">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg" data-testid="login-icon">
            <Package className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="login-title">
            Control de Producción
          </CardTitle>
          <CardDescription className="text-base" data-testid="login-description">
            Ingresa tus credenciales para continuar
          </CardDescription>
        </CardHeader>
        <CardContent>
            <div className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium" data-testid="username-label">
                Usuario / Operario
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="admin o número de operario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-12"
                data-testid="username-input"
              />
              {username && (
                <div className="text-base">
                  {fetchingInfo ? (
                    <span className="text-gray-500">Buscando operario...</span>
                  ) : operatorInfo ? (
                    <span className="text-indigo-700 font-bold text-xl">
                      {operatorInfo.nombre || `Operario ${operatorInfo.operario}`}{" "}
                      {operatorInfo.tipoTrabajo && (
                        <span className="ml-3 text-indigo-500 text-base font-semibold align-middle">
                          ({operatorInfo.tipoTrabajo === "molduras" ? "Molduras" : "Puertas"})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-red-600">Operario no encontrado</span>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium" data-testid="password-label">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
                data-testid="password-input"
              />
            </div>
            
            <Button
              type="button"
              onClick={handleSubmit}
              className="w-full h-14 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold shadow-lg flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Ingresar
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;